// src/pages/patient/MedicalHistory.jsx
import React, { useState, useEffect } from 'react';
import { 
   Pill, Activity, AlertCircle, Plus, Trash2, 
     Syringe, Stethoscope, Download, Share2, ChevronDown, ChevronUp,Users, 
} from 'lucide-react';
import Button from '../../components/ui/Button';
import Modal from '../../components/common/Modal';
import Alert from '../../components/ui/Alert';
import apiService from '../../services/api';

const MedicalHistory = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [medicalData, setMedicalData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    allergies: true,
    conditions: true,
    medications: true,
    surgeries: false,
    immunizations: false,
    family: false,
    vitals: true
  });

  // Modal states
  const [showAllergyModal, setShowAllergyModal] = useState(false);
  const [showConditionModal, setShowConditionModal] = useState(false);
  const [showMedicationModal, setShowMedicationModal] = useState(false);
  const [showSurgeryModal, setShowSurgeryModal] = useState(false);
  const [showImmunizationModal, setShowImmunizationModal] = useState(false);
  const [showFamilyHistoryModal, setShowFamilyHistoryModal] = useState(false);
  const [showVitalsModal, setShowVitalsModal] = useState(false);

  // Form states
  const [allergyForm, setAllergyForm] = useState({
    name: '',
    severity: 'moderate',
    reaction: '',
    diagnosedDate: '',
    notes: ''
  });

  const [conditionForm, setConditionForm] = useState({
    condition: '',
    diagnosedDate: '',
    status: 'active',
    notes: '',
    treatingDoctor: ''
  });

  const [medicationForm, setMedicationForm] = useState({
    name: '',
    dosage: '',
    frequency: '',
    startDate: '',
    endDate: '',
    purpose: '',
    sideEffects: ''
  });

  const [surgeryForm, setSurgeryForm] = useState({
    name: '',
    date: '',
    hospital: '',
    surgeon: '',
    notes: '',
    complications: ''
  });

  const [immunizationForm, setImmunizationForm] = useState({
    vaccine: '',
    dateGiven: '',
    nextDue: '',
    administeredBy: '',
    batchNumber: '',
    location: ''
  });

  const [familyHistoryForm, setFamilyHistoryForm] = useState({
    relation: '',
    condition: '',
    ageOfOnset: '',
    notes: ''
  });

  const [vitalsForm, setVitalsForm] = useState({
    height: '',
    weight: '',
    bloodPressureSystolic: '',
    bloodPressureDiastolic: '',
    heartRate: '',
    temperature: '',
    oxygenSaturation: ''
  });

  useEffect(() => {
    loadMedicalHistory();
  }, []);

  const loadMedicalHistory = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.get('/patients/medical-history');
      setMedicalData(response.data);
    } catch (err) {
      setError(err.message || 'Failed to load medical history');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Add Allergy
  const handleAddAllergy = async (e) => {
    e.preventDefault();
    try {
      setError('');
      await apiService.post('/patients/profile/allergies', allergyForm);
      setSuccess('Allergy added successfully');
      setShowAllergyModal(false);
      setAllergyForm({
        name: '',
        severity: 'moderate',
        reaction: '',
        diagnosedDate: '',
        notes: ''
      });
      loadMedicalHistory();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to add allergy');
    }
  };

  // Add Medical Condition
  const handleAddCondition = async (e) => {
    e.preventDefault();
    try {
      setError('');
      await apiService.post('/patients/profile/medical-history', conditionForm);
      setSuccess('Medical condition added successfully');
      setShowConditionModal(false);
      setConditionForm({
        condition: '',
        diagnosedDate: '',
        status: 'active',
        notes: '',
        treatingDoctor: ''
      });
      loadMedicalHistory();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to add condition');
    }
  };

  // Add Medication
  const handleAddMedication = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const medicationData = {
        ...medicationForm,
        sideEffects: medicationForm.sideEffects ? medicationForm.sideEffects.split(',').map(s => s.trim()) : []
      };
      await apiService.post('/patients/profile/medications', medicationData);
      setSuccess('Medication added successfully');
      setShowMedicationModal(false);
      setMedicationForm({
        name: '',
        dosage: '',
        frequency: '',
        startDate: '',
        endDate: '',
        purpose: '',
        sideEffects: ''
      });
      loadMedicalHistory();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to add medication');
    }
  };

  // Add Surgery
  const handleAddSurgery = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const surgeryData = {
        ...surgeryForm,
        complications: surgeryForm.complications ? surgeryForm.complications.split(',').map(s => s.trim()) : []
      };
      await apiService.post('/patients/profile/surgeries', surgeryData);
      setSuccess('Surgery record added successfully');
      setShowSurgeryModal(false);
      setSurgeryForm({
        name: '',
        date: '',
        hospital: '',
        surgeon: '',
        notes: '',
        complications: ''
      });
      loadMedicalHistory();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to add surgery record');
    }
  };

  // Add Immunization
  const handleAddImmunization = async (e) => {
    e.preventDefault();
    try {
      setError('');
      await apiService.post('/patients/profile/immunizations', immunizationForm);
      setSuccess('Immunization added successfully');
      setShowImmunizationModal(false);
      setImmunizationForm({
        vaccine: '',
        dateGiven: '',
        nextDue: '',
        administeredBy: '',
        batchNumber: '',
        location: ''
      });
      loadMedicalHistory();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to add immunization');
    }
  };

  // Add Family History
  const handleAddFamilyHistory = async (e) => {
    e.preventDefault();
    try {
      setError('');
      await apiService.put('/patients/profile/family-history', familyHistoryForm);
      setSuccess('Family history added successfully');
      setShowFamilyHistoryModal(false);
      setFamilyHistoryForm({
        relation: '',
        condition: '',
        ageOfOnset: '',
        notes: ''
      });
      loadMedicalHistory();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to add family history');
    }
  };

  // Update Vitals
  const handleUpdateVitals = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const vitalsData = {
        height: parseFloat(vitalsForm.height),
        weight: parseFloat(vitalsForm.weight),
        bloodPressure: vitalsForm.bloodPressureSystolic && vitalsForm.bloodPressureDiastolic ? {
          systolic: parseInt(vitalsForm.bloodPressureSystolic),
          diastolic: parseInt(vitalsForm.bloodPressureDiastolic)
        } : undefined,
        heartRate: vitalsForm.heartRate ? parseInt(vitalsForm.heartRate) : undefined,
        temperature: vitalsForm.temperature ? parseFloat(vitalsForm.temperature) : undefined,
        oxygenSaturation: vitalsForm.oxygenSaturation ? parseInt(vitalsForm.oxygenSaturation) : undefined
      };
      
      await apiService.put('/patients/profile/vitals', vitalsData);
      setSuccess('Vitals updated successfully');
      setShowVitalsModal(false);
      setVitalsForm({
        height: '',
        weight: '',
        bloodPressureSystolic: '',
        bloodPressureDiastolic: '',
        heartRate: '',
        temperature: '',
        oxygenSaturation: ''
      });
      loadMedicalHistory();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update vitals');
    }
  };

  // Delete handlers
  const handleDeleteAllergy = async (allergyId) => {
    if (!window.confirm('Are you sure you want to delete this allergy?')) return;
    
    try {
      await apiService.delete(`/patients/profile/allergies/${allergyId}`);
      setSuccess('Allergy deleted successfully');
      loadMedicalHistory();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete allergy');
    }
  };

  const handleStopMedication = async (medicationId) => {
    if (!window.confirm('Are you sure you want to stop this medication?')) return;
    
    try {
      await apiService.patch(`/patients/profile/medications/${medicationId}/stop`);
      setSuccess('Medication stopped successfully');
      loadMedicalHistory();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to stop medication');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'mild': return 'bg-yellow-100 text-yellow-800';
      case 'moderate': return 'bg-orange-100 text-orange-800';
      case 'severe': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-red-100 text-red-800';
      case 'managed': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading medical history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Medical History</h1>
            <p className="text-blue-100">Complete health records and medical information</p>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="white" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button variant="white" size="sm">
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert type="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert type="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-3 bg-red-100 rounded-lg">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">
                {medicalData?.allergies?.length || 0}
              </p>
              <p className="text-sm text-gray-500">Allergies</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Stethoscope className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">
                {medicalData?.medicalHistory?.length || 0}
              </p>
              <p className="text-sm text-gray-500">Conditions</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <Pill className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">
                {medicalData?.currentMedications?.filter(m => m.isActive)?.length || 0}
              </p>
              <p className="text-sm text-gray-500">Active Meds</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Syringe className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">
                {medicalData?.immunizations?.length || 0}
              </p>
              <p className="text-sm text-gray-500">Immunizations</p>
            </div>
          </div>
        </div>
      </div>

      {/* Blood Group & Vitals Summary */}
      {medicalData?.bloodGroup && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">Blood Group</p>
              <p className="text-2xl font-bold text-red-600">{medicalData.bloodGroup}</p>
            </div>
            {medicalData.vitals?.height && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Height</p>
                <p className="text-2xl font-bold text-gray-900">
                  {medicalData.vitals.height.value} {medicalData.vitals.height.unit}
                </p>
              </div>
            )}
            {medicalData.vitals?.weight && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Weight</p>
                <p className="text-2xl font-bold text-gray-900">
                  {medicalData.vitals.weight.value} {medicalData.vitals.weight.unit}
                </p>
              </div>
            )}
            {medicalData.vitals?.bmi && (
              <div>
                <p className="text-sm text-gray-500 mb-1">BMI</p>
                <p className="text-2xl font-bold text-gray-900">
                  {medicalData.vitals.bmi.toFixed(1)}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Allergies Section */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => toggleSection('allergies')}
              className="p-1 hover:bg-gray-100 rounded"
            >
              {expandedSections.allergies ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            <AlertCircle className="w-5 h-5 text-red-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Allergies ({medicalData?.allergies?.length || 0})
            </h3>
          </div>
          <Button size="sm" onClick={() => setShowAllergyModal(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Add Allergy
          </Button>
        </div>
        
        {expandedSections.allergies && (
          <div className="p-6">
            {medicalData?.allergies && medicalData.allergies.length > 0 ? (
              <div className="space-y-3">
                {medicalData.allergies.map((allergy) => (
                  <div key={allergy._id} className="flex items-start p-4 bg-red-50 rounded-lg border border-red-100">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="font-medium text-gray-900">{allergy.name}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(allergy.severity)}`}>
                          {allergy.severity}
                        </span>
                      </div>
                      {allergy.reaction && (
                        <p className="text-sm text-gray-600 mb-1">
                          <span className="font-medium">Reaction:</span> {allergy.reaction}
                        </p>
                      )}
                      {allergy.diagnosedDate && (
                        <p className="text-xs text-gray-500">
                          Diagnosed: {new Date(allergy.diagnosedDate).toLocaleDateString()}
                        </p>
                      )}
                      {allergy.notes && (
                        <p className="text-sm text-gray-600 mt-2">{allergy.notes}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteAllergy(allergy._id)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No allergies recorded</p>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="mt-3"
                  onClick={() => setShowAllergyModal(true)}
                >
                  Add First Allergy
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Medical Conditions Section */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => toggleSection('conditions')}
              className="p-1 hover:bg-gray-100 rounded"
            >
              {expandedSections.conditions ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            <Stethoscope className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Medical Conditions ({medicalData?.medicalHistory?.length || 0})
            </h3>
          </div>
          <Button size="sm" onClick={() => setShowConditionModal(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Add Condition
          </Button>
        </div>
        
        {expandedSections.conditions && (
          <div className="p-6">
            {medicalData?.medicalHistory && medicalData.medicalHistory.length > 0 ? (
              <div className="space-y-3">
                {medicalData.medicalHistory.map((condition) => (
                  <div key={condition._id} className="flex items-start p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="font-medium text-gray-900">{condition.condition}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(condition.status)}`}>
                          {condition.status}
                        </span>
                      </div>
                      {condition.diagnosedDate && (
                        <p className="text-xs text-gray-500 mb-2">
                          Diagnosed: {new Date(condition.diagnosedDate).toLocaleDateString()}
                        </p>
                      )}
                      {condition.treatingDoctor && (
                        <p className="text-sm text-gray-600 mb-1">
                          <span className="font-medium">Doctor:</span> Dr. {condition.treatingDoctor.firstName} {condition.treatingDoctor.lastName}
                          {condition.treatingDoctor.specialization && ` (${condition.treatingDoctor.specialization})`}
                        </p>
                      )}
                      {condition.notes && (
                        <p className="text-sm text-gray-600 mt-2">{condition.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Stethoscope className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No medical conditions recorded</p>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="mt-3"
                  onClick={() => setShowConditionModal(true)}
                >
                  Add First Condition
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Current Medications Section */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => toggleSection('medications')}
              className="p-1 hover:bg-gray-100 rounded"
            >
              {expandedSections.medications ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            <Pill className="w-5 h-5 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Current Medications ({medicalData?.currentMedications?.filter(m => m.isActive)?.length || 0})
            </h3>
          </div>
          <Button size="sm" onClick={() => setShowMedicationModal(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Add Medication
          </Button>
        </div>
        
        {expandedSections.medications && (
          <div className="p-6">
            {medicalData?.currentMedications && medicalData.currentMedications.filter(m => m.isActive).length > 0 ? (
              <div className="space-y-3">
                {medicalData.currentMedications.filter(m => m.isActive).map((medication) => (
                  <div key={medication._id} className="flex items-start p-4 bg-green-50 rounded-lg border border-green-100">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="font-medium text-gray-900">{medication.name}</h4>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-2">
                        <p><span className="font-medium">Dosage:</span> {medication.dosage}</p>
                        <p><span className="font-medium">Frequency:</span> {medication.frequency}</p>
                      </div>
                      {medication.purpose && (
                        <p className="text-sm text-gray-600 mb-1">
                          <span className="font-medium">Purpose:</span> {medication.purpose}
                        </p>
                      )}
                      {medication.prescribedBy && (
                        <p className="text-xs text-gray-500">
                          Prescribed by: Dr. {medication.prescribedBy.firstName} {medication.prescribedBy.lastName}
                        </p>
                      )}
                      {medication.startDate && (
                        <p className="text-xs text-gray-500 mt-1">
                          Started: {new Date(medication.startDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleStopMedication(medication._id)}
                      className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      Stop
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Pill className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No current medications</p>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="mt-3"
                  onClick={() => setShowMedicationModal(true)}
                >
                  Add First Medication
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Surgeries Section */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => toggleSection('surgeries')}
              className="p-1 hover:bg-gray-100 rounded"
            >
              {expandedSections.surgeries ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            <Activity className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Surgeries ({medicalData?.surgeries?.length || 0})
            </h3>
          </div>
          <Button size="sm" onClick={() => setShowSurgeryModal(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Add Surgery
          </Button>
        </div>
        
        {expandedSections.surgeries && (
          <div className="p-6">
            {medicalData?.surgeries && medicalData.surgeries.length > 0 ? (
              <div className="space-y-3">
                {medicalData.surgeries.map((surgery) => (
                  <div key={surgery._id} className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                    <h4 className="font-medium text-gray-900 mb-2">{surgery.name}</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                      {surgery.date && (
                        <p><span className="font-medium">Date:</span> {new Date(surgery.date).toLocaleDateString()}</p>
                      )}
                      {surgery.hospital && (
                        <p><span className="font-medium">Hospital:</span> {surgery.hospital.name || surgery.hospital}</p>
                      )}
                      {surgery.surgeon && (
                        <p><span className="font-medium">Surgeon:</span> Dr. {surgery.surgeon.firstName} {surgery.surgeon.lastName}</p>
                      )}
                    </div>
                    {surgery.notes && (
                      <p className="text-sm text-gray-600 mt-2">{surgery.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No surgery records</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Immunizations Section */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => toggleSection('immunizations')}
              className="p-1 hover:bg-gray-100 rounded"
            >
              {expandedSections.immunizations ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            <Syringe className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Immunizations ({medicalData?.immunizations?.length || 0})
            </h3>
          </div>
          <Button size="sm" onClick={() => setShowImmunizationModal(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Add Immunization
          </Button>
        </div>
        
        {expandedSections.immunizations && (
          <div className="p-6">
            {medicalData?.immunizations && medicalData.immunizations.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {medicalData.immunizations.map((immunization) => (
                  <div key={immunization._id} className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                    <h4 className="font-medium text-gray-900 mb-2">{immunization.vaccine}</h4>
                    <p className="text-sm text-gray-600 mb-1">
                      <span className="font-medium">Date:</span> {new Date(immunization.dateGiven).toLocaleDateString()}
                    </p>
                    {immunization.nextDue && (
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Next Due:</span> {new Date(immunization.nextDue).toLocaleDateString()}
                      </p>
                    )}
                    {immunization.batchNumber && (
                      <p className="text-xs text-gray-500 mt-2">Batch: {immunization.batchNumber}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Syringe className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No immunization records</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Family History Section */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => toggleSection('family')}
              className="p-1 hover:bg-gray-100 rounded"
            >
              {expandedSections.family ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            <Users className="w-5 h-5 text-orange-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Family History ({medicalData?.familyHistory?.length || 0})
            </h3>
          </div>
          <Button size="sm" onClick={() => setShowFamilyHistoryModal(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Add Family History
          </Button>
        </div>
        
        {expandedSections.family && (
          <div className="p-6">
            {medicalData?.familyHistory && medicalData.familyHistory.length > 0 ? (
              <div className="space-y-3">
                {medicalData.familyHistory.map((history, index) => (
                  <div key={index} className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{history.condition}</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">Relation:</span> {history.relation}
                        </p>
                        {history.ageOfOnset && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Age of Onset:</span> {history.ageOfOnset}
                          </p>
                        )}
                        {history.notes && (
                          <p className="text-sm text-gray-600 mt-2">{history.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No family history recorded</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Vitals Section */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => toggleSection('vitals')}
              className="p-1 hover:bg-gray-100 rounded"
            >
              {expandedSections.vitals ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            <Activity className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-semibold text-gray-900">Current Vitals</h3>
          </div>
          <Button size="sm" onClick={() => setShowVitalsModal(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Update Vitals
          </Button>
        </div>
        
        {expandedSections.vitals && medicalData?.vitals && (
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {medicalData.vitals.bloodPressure && (
                <div className="p-4 bg-teal-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Blood Pressure</p>
                  <p className="text-xl font-bold text-gray-900">
                    {medicalData.vitals.bloodPressure.systolic}/{medicalData.vitals.bloodPressure.diastolic}
                  </p>
                  <p className="text-xs text-gray-500">mmHg</p>
                </div>
              )}
              {medicalData.vitals.heartRate && (
                <div className="p-4 bg-teal-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Heart Rate</p>
                  <p className="text-xl font-bold text-gray-900">
                    {medicalData.vitals.heartRate.value}
                  </p>
                  <p className="text-xs text-gray-500">bpm</p>
                </div>
              )}
              {medicalData.vitals.temperature && (
                <div className="p-4 bg-teal-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Temperature</p>
                  <p className="text-xl font-bold text-gray-900">
                    {medicalData.vitals.temperature.value}
                  </p>
                  <p className="text-xs text-gray-500">{medicalData.vitals.temperature.unit}</p>
                </div>
              )}
              {medicalData.vitals.oxygenSaturation && (
                <div className="p-4 bg-teal-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Oxygen Saturation</p>
                  <p className="text-xl font-bold text-gray-900">
                    {medicalData.vitals.oxygenSaturation.value}%
                  </p>
                  <p className="text-xs text-gray-500">SpO2</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      
      {/* Add Allergy Modal */}
      <Modal
        isOpen={showAllergyModal}
        onClose={() => setShowAllergyModal(false)}
        title="Add Allergy"
      >
        <form onSubmit={handleAddAllergy} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Allergy Name *
            </label>
            <input
              type="text"
              required
              value={allergyForm.name}
              onChange={(e) => setAllergyForm({ ...allergyForm, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., Penicillin, Peanuts"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Severity *
            </label>
            <select
              required
              value={allergyForm.severity}
              onChange={(e) => setAllergyForm({ ...allergyForm, severity: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="mild">Mild</option>
              <option value="moderate">Moderate</option>
              <option value="severe">Severe</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reaction
            </label>
            <input
              type="text"
              value={allergyForm.reaction}
              onChange={(e) => setAllergyForm({ ...allergyForm, reaction: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., Rash, Difficulty breathing"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Diagnosed Date
            </label>
            <input
              type="date"
              value={allergyForm.diagnosedDate}
              onChange={(e) => setAllergyForm({ ...allergyForm, diagnosedDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={allergyForm.notes}
              onChange={(e) => setAllergyForm({ ...allergyForm, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Additional information..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAllergyModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Add Allergy</Button>
          </div>
        </form>
      </Modal>

      {/* Add Condition Modal */}
      <Modal
        isOpen={showConditionModal}
        onClose={() => setShowConditionModal(false)}
        title="Add Medical Condition"
      >
        <form onSubmit={handleAddCondition} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Condition Name *
            </label>
            <input
              type="text"
              required
              value={conditionForm.condition}
              onChange={(e) => setConditionForm({ ...conditionForm, condition: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., Hypertension, Diabetes"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status *
            </label>
            <select
              required
              value={conditionForm.status}
              onChange={(e) => setConditionForm({ ...conditionForm, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="active">Active</option>
              <option value="managed">Managed</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Diagnosed Date
            </label>
            <input
              type="date"
              value={conditionForm.diagnosedDate}
              onChange={(e) => setConditionForm({ ...conditionForm, diagnosedDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={conditionForm.notes}
              onChange={(e) => setConditionForm({ ...conditionForm, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Additional information..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConditionModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Add Condition</Button>
          </div>
        </form>
      </Modal>

      {/* Add Medication Modal */}
      <Modal
        isOpen={showMedicationModal}
        onClose={() => setShowMedicationModal(false)}
        title="Add Medication"
      >
        <form onSubmit={handleAddMedication} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Medication Name *
            </label>
            <input
              type="text"
              required
              value={medicationForm.name}
              onChange={(e) => setMedicationForm({ ...medicationForm, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., Aspirin"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dosage *
              </label>
              <input
                type="text"
                required
                value={medicationForm.dosage}
                onChange={(e) => setMedicationForm({ ...medicationForm, dosage: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., 100mg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Frequency *
              </label>
              <input
                type="text"
                required
                value={medicationForm.frequency}
                onChange={(e) => setMedicationForm({ ...medicationForm, frequency: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., Once daily"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={medicationForm.startDate}
                onChange={(e) => setMedicationForm({ ...medicationForm, startDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={medicationForm.endDate}
                onChange={(e) => setMedicationForm({ ...medicationForm, endDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purpose
            </label>
            <input
              type="text"
              value={medicationForm.purpose}
              onChange={(e) => setMedicationForm({ ...medicationForm, purpose: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., Pain relief"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Side Effects (comma-separated)
            </label>
            <input
              type="text"
              value={medicationForm.sideEffects}
              onChange={(e) => setMedicationForm({ ...medicationForm, sideEffects: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., Nausea, Dizziness"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowMedicationModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Add Medication</Button>
          </div>
        </form>
      </Modal>

      {/* Add Surgery Modal */}
      <Modal
        isOpen={showSurgeryModal}
        onClose={() => setShowSurgeryModal(false)}
        title="Add Surgery Record"
      >
        <form onSubmit={handleAddSurgery} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Surgery Name *
            </label>
            <input
              type="text"
              required
              value={surgeryForm.name}
              onChange={(e) => setSurgeryForm({ ...surgeryForm, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., Appendectomy"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date *
            </label>
            <input
              type="date"
              required
              value={surgeryForm.date}
              onChange={(e) => setSurgeryForm({ ...surgeryForm, date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hospital
            </label>
            <input
              type="text"
              value={surgeryForm.hospital}
              onChange={(e) => setSurgeryForm({ ...surgeryForm, hospital: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Hospital name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Surgeon
            </label>
            <input
              type="text"
              value={surgeryForm.surgeon}
              onChange={(e) => setSurgeryForm({ ...surgeryForm, surgeon: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Surgeon name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={surgeryForm.notes}
              onChange={(e) => setSurgeryForm({ ...surgeryForm, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Additional information..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowSurgeryModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Add Surgery</Button>
          </div>
        </form>
      </Modal>

      {/* Add Immunization Modal */}
      <Modal
        isOpen={showImmunizationModal}
        onClose={() => setShowImmunizationModal(false)}
        title="Add Immunization"
      >
        <form onSubmit={handleAddImmunization} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vaccine Name *
            </label>
            <input
              type="text"
              required
              value={immunizationForm.vaccine}
              onChange={(e) => setImmunizationForm({ ...immunizationForm, vaccine: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., COVID-19, Influenza"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Given *
              </label>
              <input
                type="date"
                required
                value={immunizationForm.dateGiven}
                onChange={(e) => setImmunizationForm({ ...immunizationForm, dateGiven: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Next Due
              </label>
              <input
                type="date"
                value={immunizationForm.nextDue}
                onChange={(e) => setImmunizationForm({ ...immunizationForm, nextDue: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Administered By
            </label>
            <input
              type="text"
              value={immunizationForm.administeredBy}
              onChange={(e) => setImmunizationForm({ ...immunizationForm, administeredBy: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Healthcare provider name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Batch Number
            </label>
            <input
              type="text"
              value={immunizationForm.batchNumber}
              onChange={(e) => setImmunizationForm({ ...immunizationForm, batchNumber: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Vaccine batch number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location
            </label>
            <input
              type="text"
              value={immunizationForm.location}
              onChange={(e) => setImmunizationForm({ ...immunizationForm, location: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Where it was administered"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowImmunizationModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Add Immunization</Button>
          </div>
        </form>
      </Modal>

      {/* Add Family History Modal */}
      <Modal
        isOpen={showFamilyHistoryModal}
        onClose={() => setShowFamilyHistoryModal(false)}
        title="Add Family History"
      >
        <form onSubmit={handleAddFamilyHistory} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Relation *
            </label>
            <select
              required
              value={familyHistoryForm.relation}
              onChange={(e) => setFamilyHistoryForm({ ...familyHistoryForm, relation: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Select relation</option>
              <option value="mother">Mother</option>
              <option value="father">Father</option>
              <option value="sibling">Sibling</option>
              <option value="grandparent">Grandparent</option>
              <option value="aunt">Aunt</option>
              <option value="uncle">Uncle</option>
              <option value="cousin">Cousin</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Condition *
            </label>
            <input
              type="text"
              required
              value={familyHistoryForm.condition}
              onChange={(e) => setFamilyHistoryForm({ ...familyHistoryForm, condition: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., Diabetes, Heart Disease"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Age of Onset
            </label>
            <input
              type="number"
              value={familyHistoryForm.ageOfOnset}
              onChange={(e) => setFamilyHistoryForm({ ...familyHistoryForm, ageOfOnset: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Age when diagnosed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={familyHistoryForm.notes}
              onChange={(e) => setFamilyHistoryForm({ ...familyHistoryForm, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Additional information..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowFamilyHistoryModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Add Family History</Button>
          </div>
        </form>
      </Modal>

      {/* Update Vitals Modal */}
      <Modal
        isOpen={showVitalsModal}
        onClose={() => setShowVitalsModal(false)}
        title="Update Vitals"
      >
        <form onSubmit={handleUpdateVitals} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Height (cm)
              </label>
              <input
                type="number"
                step="0.1"
                value={vitalsForm.height}
                onChange={(e) => setVitalsForm({ ...vitalsForm, height: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="170"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Weight (kg)
              </label>
              <input
                type="number"
                step="0.1"
                value={vitalsForm.weight}
                onChange={(e) => setVitalsForm({ ...vitalsForm, weight: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="70"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Blood Pressure (mmHg)
            </label>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="number"
                value={vitalsForm.bloodPressureSystolic}
                onChange={(e) => setVitalsForm({ ...vitalsForm, bloodPressureSystolic: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Systolic (120)"
              />
              <input
                type="number"
                value={vitalsForm.bloodPressureDiastolic}
                onChange={(e) => setVitalsForm({ ...vitalsForm, bloodPressureDiastolic: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Diastolic (80)"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Heart Rate (bpm)
              </label>
              <input
                type="number"
                value={vitalsForm.heartRate}
                onChange={(e) => setVitalsForm({ ...vitalsForm, heartRate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="72"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Temperature (°C)
              </label>
              <input
                type="number"
                step="0.1"
                value={vitalsForm.temperature}
                onChange={(e) => setVitalsForm({ ...vitalsForm, temperature: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="36.5"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Oxygen Saturation (%)
            </label>
            <input
              type="number"
              value={vitalsForm.oxygenSaturation}
              onChange={(e) => setVitalsForm({ ...vitalsForm, oxygenSaturation: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="98"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowVitalsModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Update Vitals</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default MedicalHistory;